import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Paper,
  Divider,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { 
  Work as WorkIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';

interface ProjectCardProps {
  title: string;
  mainCategory: string;
  projectDuration: string;
  budget: string;
  client: string;
  currentStatus: string;
  startDeadline: string;
  budgetType: string;
  preferredContractorType: string;
  description: string;
  onViewFull: () => void;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  mainCategory,
  projectDuration,
  budget,
  client,
  currentStatus,
  startDeadline,
  budgetType,
  preferredContractorType,
  description,
  onViewFull,
}) => {
  const { t } = useTranslation();


  return (
    <Card
      elevation={0}
      sx={{
        width: 'calc(100% - 25px)',
        borderRadius: 3,
        overflow: 'hidden',
        background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        '&:hover': {
          transform: 'translateY(-4px) scale(1.01)',
          boxShadow: '0 12px 24px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(0, 0, 0, 0.1)',
          '& .project-header': {
            background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          },
          '& .floating-icon': {
            transform: 'scale(1.1) rotate(5deg)',
          }
        }
      }}
    >
      {/* Floating Action Button */}
      <Tooltip title={t('projects.addToFavorites')} placement="top">
        <IconButton
          className="floating-icon"
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            zIndex: 2,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: '#ff6b6b',
              color: 'white',
              transform: 'scale(1.1)',
            }
          }}
        >
          <StarIcon sx={{ color: '#ffffff' }} />
        </IconButton>
      </Tooltip>

      {/* Header with Gradient */}
      <Box
        className="project-header"
        sx={{
          background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
          p: 3,
          color: '#ffffff',
          position: 'relative',
          transition: 'all 0.4s ease',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'linear-gradient(45deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
            pointerEvents: 'none',
          }
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Avatar
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
            }}
          >
            <WorkIcon sx={{ color: '#ffffff' }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: '1.25rem',
                lineHeight: 1.3,
                mb: 1,
                textShadow: '0 2px 4px rgba(0,0,0,0.1)',
              }}
            >
              {title}
            </Typography>
            <Chip
              label={mainCategory}
              size="medium"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                fontWeight: 600,
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
              }}
            />
          </Box>
        </Stack>
      </Box>

      {/* Content with Modern Layout */}
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ p: 3 }}>
          {/* Project Details - 2 rows x 4 columns */}
          <Grid container spacing={2} sx={{ mb: 3 }} marginTop={1}>
            {/* Row 1 */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Основна категория
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mt: 0.5 }}>
                  {mainCategory}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Продължителност на проекта
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mt: 0.5 }}>
                  {projectDuration}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Бюджет на проекта
                </Typography>
                <Typography variant="body2" sx={{ color: '#10b981', fontWeight: 700, mt: 0.5 }}>
                  {budget}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Клиент
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mt: 0.5 }}>
                  {client}
                </Typography>
              </Box>
            </Grid>
            
            {/* Row 2 */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Текущ статус на проекта
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mt: 0.5 }}>
                  {currentStatus}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Срок за започване
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mt: 0.5 }}>
                  {startDeadline}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Тип на бюджета
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mt: 0.5 }}>
                  {budgetType}
                </Typography>
              </Box>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ p: 2, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: 2, border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                <Typography variant="caption" sx={{ color: '#a0a0a0', fontWeight: 600, textTransform: 'uppercase' }}>
                  Предпочитан тип изпълнител
                </Typography>
                <Typography variant="body2" sx={{ color: '#ffffff', fontWeight: 500, mt: 0.5 }}>
                  {preferredContractorType}
                </Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2, borderColor: 'rgba(255, 255, 255, 0.1)' }} />

          {/* Project Description */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                color: '#a0a0a0',
                lineHeight: 1.7,
                fontSize: '1rem',
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontStyle: 'italic',
              }}
            >
              {description}
            </Typography>
          </Box>

          {/* Call to Action */}
          <Box sx={{ textAlign: 'center' }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                backgroundColor: 'rgba(40, 160, 240, 0.1)',
                border: '1px solid rgba(40, 160, 240, 0.3)',
                borderRadius: 3,
                mb: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#28A0F0',
                  fontWeight: 600,
                  fontSize: '0.9rem',
                  lineHeight: 1.5,
                }}
              >
                За да получите достъп до пълната информация за проекта, както и до данните за контакт на клиента, трябва да заплатите посочената в маркетплейса цена в кредити.
              </Typography>
            </Paper>
            
            <Button
              variant="contained"
              size="medium"
              endIcon={<VisibilityIcon sx={{ color: '#ffffff' }} />}
              onClick={onViewFull}
              sx={{
                background: 'linear-gradient(135deg, #28A0F0 0%, #ec4899 100%)',
                color: '#ffffff',
                px: 4,
                py: 1.5,
                fontWeight: 700,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: '0 6px 24px rgba(40, 160, 240, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #be185d 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 32px rgba(40, 160, 240, 0.4)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}
            >
              {t('projects.viewFullButton')}
            </Button>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ProjectCard;
